import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiClient } from '@/lib/api/client.ts'
import { authStore } from '@/store/authStore.ts'
import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { PasswordResetSuccessModal } from '@/components/PasswordResetSuccessModal.tsx'

/**
 * RESET PASSWORD PAGE
 *
 * With the BFF architecture, the /api/auth/callback route handles token exchange
 * and redirects here with ?recovery=true query param when the session is established.
 * The user's recovery session is stored in httpOnly cookies by the callback handler.
 */
export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  // Post-success state: drives the celebratory + app-download modal. The
  // modal owns dismissal UX; navigation to /subscriptions/manage runs in its
  // onContinue callback below so the user always lands there regardless of
  // how they close the modal (X, click-outside, Escape, or the CTA).
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successEmail, setSuccessEmail] = useState<string | null>(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Three entry points:
    // 1. ?recovery=true    → came back from /api/auth/callback; session cookies set
    // 2. ?token_hash=X&type=recovery → email link pointed here directly (e.g. prod env
    //    PASSWORD_RESET_REDIRECT_URL points at /auth/reset-password instead of
    //    /api/auth/callback). Forward to the BFF callback so it can verifyOtp and
    //    set session cookies, then we'll bounce back with ?recovery=true.
    // 3. No params         → rely on existing recovery session in authStore
    const isRecovery = searchParams.get('recovery') === 'true'
    const tokenHash = searchParams.get('token_hash')
    const tokenType = searchParams.get('type')

    if (tokenHash && tokenType) {
      const qs = new URLSearchParams({ token_hash: tokenHash, type: tokenType }).toString()
      window.location.replace(`/api/auth/callback?${qs}`)
      return
    }

    if (isRecovery) {
      // Recovery session is set via cookies by /api/auth/callback
      setIsReady(true)
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname)
    } else {
      // Check if we have an active recovery session from auth store
      const state = authStore.state
      if (state.isRecoverySession && state.user) {
        setIsReady(true)
      } else {
        // No valid recovery session - redirect to auth
        navigate('/auth', { replace: true })
      }
    }
  }, [navigate, searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      // Update password via server (uses the recovery session from cookies)
      await ApiClient.resetPassword(password)

      // Capture email *before* mutating the store (defensive: store changes
      // shouldn't drop user.email today, but this keeps the modal copy stable
      // even if that invariant ever changes).
      const emailForModal = authStore.state.user?.email ?? null

      // Update auth store
      authStore.setState(state => ({
        ...state,
        didSetPassword: true,
        isRecoverySession: false,
        emailVerified: true,
      }))

      // Show the celebratory modal. Navigation now happens in the modal's
      // onContinue handler (rendered below) instead of inline here so that
      // dismissing via X / click-outside also routes the user forward.
      setSuccessEmail(emailForModal)
      setShowSuccessModal(true)
    } catch (err) {
      console.error('[RESET PASSWORD] Error:', err)
      setError(err instanceof Error ? err.message : 'Failed to reset password')

      // Sign out on error
      try { await ApiClient.signOut() } catch { /* ignore */ }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
            <p className="text-center mt-4 text-muted-foreground">
              Verifying reset link...
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Reset Your Password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  minLength={6}
                  required
                  autoComplete="new-password"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Must be at least 6 characters long
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                After resetting your password, you'll be automatically signed in
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      <PasswordResetSuccessModal
        open={showSuccessModal}
        email={successEmail}
        onContinue={() => {
          setShowSuccessModal(false)
          navigate('/subscriptions/manage')
        }}
      />
    </>
  )
}
